import React, { useState } from 'react';
import AccountDestinazioneBadge from '../components/AccountDestinazioneBadge';
import DriveFlagBadge from '../components/DriveFlagBadge';
import OpusBadge from '../components/OpusBadge';
import { useNavigate } from 'react-router-dom';
import { Check, Eye, Image, Target, Scissors, ArrowLeft, CheckCircle, CalendarClock, Film, Link as LinkIcon, Undo2, Megaphone } from 'lucide-react';
import { AnimatedModal } from '../components/AnimatedComponents';
import { useData } from '../contexts/DataContext';
import { Contenuto, StatoContenuto } from '../types';
import StatusBadge from '../components/StatusBadge';
import toast from 'react-hot-toast';
import PageHeader from '../components/ui/PageHeader';
import StatsCard from '../components/ui/StatsCard';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { usePageState } from '../hooks/usePageState';
import { daMontare, reelMontatiNelMese } from '../utils/editing';

// Pagina "Editing": fase tra Videomaker e Programmazione.
// Un contenuto arriva qui quando il videomaker lo segna "Registrato" e ne esce
// quando il video editor lo segna "Montato" (flag videoMontato, come
// videoRegistrato non tocca lo stato). Programmare non richiede il montaggio:
// un contenuto già programmato ma non montato resta in coda qui, con la data
// di uscita in evidenza. Stesso impianto della pagina Videomaker: griglia
// clienti → contenuti del cliente, due schede "Da montare" / "Montati".
const Editing: React.FC = () => {
  const navigate = useNavigate();
  const { state, updateContenuto, getClienteById, loadContenuti } = useData();
  // Cliente aperto nell'URL: indietro e refresh riportano allo stesso punto.
  // `vista` = '' (da montare) oppure 'montati' (montati nel mese).
  const [pageState, setPageState] = usePageState({ client: '', vista: '' }, { push: ['client'] });
  const selectedClientId = pageState.client || null;
  const vistaMontati = pageState.vista === 'montati';
  // Contenuto aperto nel modal (anteprima script + link montaggio). In sola
  // lettura dalla scheda "Montati": lì non c'è nulla da confermare. Non si
  // rimanda alla scheda del Videomaker perché il suo pulsante "Registrato" è
  // un toggle e su un contenuto già registrato lo annullerebbe.
  const [montaggio, setMontaggio] = useState<Contenuto | null>(null);
  const [soloLettura, setSoloLettura] = useState(false);
  const [linkMontaggio, setLinkMontaggio] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Clienti archiviati (status !== 'active'): tutto il loro workflow è escluso e
  // non conteggiato, come nel Videomaker.
  const clientiAttiviIds = new Set(
    state.clienti.filter(c => c.status === 'active').map(c => c.id)
  );

  // Coda di editing: registrati e non ancora montati (vedi utils/editing.ts)
  const contenutiBase = state.contenuti.filter(
    contenuto => clientiAttiviIds.has(contenuto.clienteId) && daMontare(contenuto)
  );

  const apriMontaggio = (contenuto: Contenuto, lettura = false) => {
    setMontaggio(contenuto);
    setSoloLettura(lettura);
    setLinkMontaggio(contenuto.linkMontaggio || '');
  };

  const chiudiMontaggio = () => {
    setMontaggio(null);
    setSoloLettura(false);
    setLinkMontaggio('');
  };

  // "Montato": flag videoMontato + eventuale link del video montato.
  // Il link passa dalla validazione isURL del backend: si manda solo se c'è.
  const confermaMontato = async () => {
    if (!montaggio) return;
    const link = linkMontaggio.trim();
    if (link && !/^https?:\/\/\S+$/i.test(link)) {
      toast.error('Il link del video montato non è un indirizzo valido');
      return;
    }
    setSalvando(true);
    try {
      await updateContenuto(montaggio.id, {
        videoMontato: true,
        ...(link ? { linkMontaggio: link } : {}),
      });
      await loadContenuti();
      toast.success(
        montaggio.stato === StatoContenuto.PROGRAMMATO
          ? 'Montato: il contenuto è già programmato'
          : 'Montato: lo trovi in "Da programmare"'
      );
      chiudiMontaggio();
    } catch (error) {
      console.error('Errore nel segnare come montato:', error);
      toast.error('Errore nell\'aggiornamento. Riprova.');
    } finally {
      setSalvando(false);
    }
  };

  // Ripensamento: torna in coda "da montare" senza toccare registrato/programmazione.
  const annullaMontato = async (contenuto: Contenuto) => {
    try {
      await updateContenuto(contenuto.id, { videoMontato: false });
      await loadContenuti();
      toast.success('Riportato in "Da montare"');
    } catch (error) {
      console.error('Errore nell\'annullare il montaggio:', error);
      toast.error('Errore nell\'aggiornamento. Riprova.');
    }
  };

  // "Programma": come nel Videomaker, apre Programmazione sulla vista "da
  // programmare" filtrata sul cliente. Un contenuto registrato è già in quella
  // coda (videoRegistrato basta), salvo che sia stato tolto col pulsante
  // Rimuovi: in quel caso lo si rimanda con prontoDaProgrammare, che il backend
  // usa per azzerare removed_from_scheduling.
  const handleProgramma = async (contenuto: Contenuto) => {
    try {
      if (contenuto.rimossoDaProgrammazione) {
        await updateContenuto(contenuto.id, { prontoDaProgrammare: true });
        await loadContenuti();
        toast.success('Rimesso in "da programmare"');
      }
      navigate(`/publisher?view=daProgrammare&client=${contenuto.clienteId}`);
    } catch (error) {
      console.error('Errore nello spostare in da programmare:', error);
      toast.error('Errore. Riprova.');
    }
  };

  const handleSelectClient = (clientId: string, vista: '' | 'montati' = '') =>
    setPageState({ client: clientId, vista });
  const handleBackToClients = () => setPageState({ client: '', vista: '' });
  const handleCambiaVista = (vista: '' | 'montati') => setPageState({ vista });

  // Mese corrente: serve ai badge delle card (vista 1) e alla lista "Montati"
  // del cliente (vista 2).
  const nowEd = new Date();
  const meseCorrente = nowEd.getMonth();
  const annoCorrente = nowEd.getFullYear();
  const nomeMese = nowEd.toLocaleDateString('it-IT', { month: 'long' });

  const dataUscita = (contenuto: Contenuto) =>
    contenuto.dataPubblicazione
      ? new Date(contenuto.dataPubblicazione).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })
      : null;

  // Modal "Montato": anteprima dello script + link del video montato (opzionale)
  const montaggioModal = (
    <AnimatedModal
      isOpen={montaggio !== null}
      onClose={chiudiMontaggio}
      title={soloLettura ? 'Contenuto' : 'Montaggio'}
      size="lg"
    >
      {montaggio && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight" style={{ letterSpacing: '-0.02em' }}>
                {montaggio.titolo}
              </h3>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {getClienteById(montaggio.clienteId)?.nomeAzienda}
                {dataUscita(montaggio) && montaggio.stato === StatoContenuto.PROGRAMMATO && ` · esce il ${dataUscita(montaggio)}`}
              </p>
            </div>
            <StatusBadge stato={montaggio.stato} />
          </div>

          {/* Script: gancio / corpo / CTA, così il video editor monta sul testo giusto */}
          <div className="space-y-2">
            {[
              { label: 'Gancio', value: montaggio.hook, accent: 'text-amber-600 dark:text-amber-400' },
              { label: 'Corpo', value: montaggio.body, accent: 'text-blue-600 dark:text-blue-400' },
              { label: 'CTA', value: montaggio.cta, accent: 'text-emerald-600 dark:text-emerald-400' },
            ].map((s) => (
              <div key={s.label} className="bg-slate-50 dark:bg-gray-700/50 p-3 rounded-xl border border-slate-200 dark:border-gray-600">
                <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${s.accent}`}>{s.label}</div>
                <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
                  {s.value || '—'}
                </p>
              </div>
            ))}
          </div>

          {(montaggio.linkIspirazione || montaggio.copertinaUrl || montaggio.copyPost) && (
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {montaggio.linkIspirazione && (
                <a href={montaggio.linkIspirazione} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:underline">
                  Riferimento
                </a>
              )}
              {montaggio.copertinaUrl && (
                <a href={montaggio.copertinaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:underline">
                  <Image size={11} /> Copertina
                </a>
              )}
              {montaggio.copyPost && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">Copy</span>
              )}
            </div>
          )}

          {soloLettura ? (
            montaggio.linkMontaggio && (
              <div className="text-xs sm:text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">Video montato:</span>{' '}
                <a href={montaggio.linkMontaggio} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium underline break-all">
                  Apri link
                </a>
              </div>
            )
          ) : (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100 mb-1.5">
                Link del video montato <span className="text-slate-400 font-normal">(opzionale)</span>
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="url"
                  value={linkMontaggio}
                  onChange={(e) => setLinkMontaggio(e.target.value)}
                  placeholder="https://drive.google.com/file/..."
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-200 dark:border-gray-600 rounded-xl focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 text-slate-900 dark:text-slate-100 bg-white dark:bg-gray-700 text-base sm:text-sm"
                />
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-1">
            <Button variant="secondary" onClick={chiudiMontaggio} className="w-full sm:w-auto justify-center min-h-[44px]">
              Chiudi
            </Button>
            {!soloLettura && (
              <Button variant="primary" disabled={salvando} onClick={confermaMontato} className="w-full sm:w-auto justify-center min-h-[44px]">
                <Check className="h-4 w-4" /> Montato
              </Button>
            )}
          </div>
        </div>
      )}
    </AnimatedModal>
  );

  // ===== VISTA 1: lista clienti attivi =====
  if (!selectedClientId) {
    const clientiAttivi = state.clienti
      .filter(c => c.status === 'active')
      .sort((a, b) => a.nomeAzienda.localeCompare(b.nomeAzienda, 'it', { sensitivity: 'base' }));

    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
          <PageHeader
            title="Editing"
            description="Contenuti registrati da montare"
          />
          <StatsCard
            value={contenutiBase.length}
            label="contenuti da montare"
            variant="warning"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {clientiAttivi.length === 0 ? (
            <div className="col-span-full">
              <Card className="p-4 sm:p-6 md:p-8 text-center">
                <Scissors className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400" />
                <h3 className="mt-2 text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>Nessun cliente attivo</h3>
                <p className="mt-1 text-sm sm:text-base text-slate-500">I clienti attivi appariranno qui</p>
              </Card>
            </div>
          ) : (
            clientiAttivi.map((cliente) => {
              const contenutiCliente = state.contenuti.filter(c => c.clienteId === cliente.id);
              const daMontareCliente = contenutiBase.filter(c => c.clienteId === cliente.id);
              // Urgenza: c'è un contenuto programmato ma non ancora montato,
              // cioè con una data di uscita che si avvicina senza il video pronto.
              const programmatiNonMontati = daMontareCliente.filter(c => c.stato === StatoContenuto.PROGRAMMATO);
              const prossimaUscita = programmatiNonMontati
                .map(c => c.dataPubblicazione ? new Date(c.dataPubblicazione).getTime() : Infinity)
                .reduce((min, t) => Math.min(min, t), Infinity);
              const urgente = prossimaUscita !== Infinity && prossimaUscita - nowEd.getTime() < 3 * 24 * 60 * 60 * 1000;
              const montatiMese = reelMontatiNelMese(contenutiCliente, meseCorrente, annoCorrente).length;
              // Bordo verde e spunta solo a obiettivo raggiunto: montati nel mese
              // >= reel previsti dal cliente (come "obiettivoRaggiunto" sul
              // Videomaker con i programmati). Una coda vuota da sola non basta.
              const tuttoMontato = (cliente.reelMensili || 0) > 0 && montatiMese >= (cliente.reelMensili || 0);
              return (
                <div
                  key={cliente.id}
                  onClick={() => handleSelectClient(cliente.id)}
                  className={`block cursor-pointer p-3 sm:p-4 rounded-xl border transition-all duration-200 hover:shadow-md bg-white dark:bg-gray-800 ${
                    urgente
                      ? 'border-red-400/70 dark:border-red-500/50 ring-1 ring-red-400/15 hover:border-red-400 dark:hover:border-red-500'
                      : tuttoMontato
                      ? 'border-emerald-400/70 dark:border-emerald-500/50 ring-1 ring-emerald-400/15 hover:border-emerald-400 dark:hover:border-emerald-500'
                      : 'border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600'
                  }`}
                >
                  {urgente && (
                    <div
                      className="flex items-center gap-1 mb-2 text-[9px] sm:text-[10px] font-semibold text-red-500 dark:text-red-400"
                      title="Un contenuto programmato esce entro 3 giorni e non è ancora montato"
                    >
                      <CalendarClock className="w-2.5 h-2.5 flex-shrink-0" /> Uscita vicina, video da montare
                    </div>
                  )}
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 dark:bg-gray-700 flex-shrink-0 overflow-hidden">
                      {cliente.logoUrl ? (
                        <img src={cliente.logoUrl} alt={cliente.nomeAzienda} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500 text-xs sm:text-sm font-medium">
                          {cliente.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 text-xs sm:text-sm truncate">
                        {cliente.nomeAzienda}
                      </h4>
                      <div className="flex items-center gap-1 mt-0.5 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                        <Target className="w-3 h-3 flex-shrink-0" />
                        <span><span className="font-semibold text-slate-700 dark:text-slate-300">{cliente.reelMensili || 0}</span> reel previsti</span>
                      </div>
                    </div>
                    {tuttoMontato && (
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                    )}
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    {/* Da montare — arancione: registrati in attesa del montaggio */}
                    <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200/60 dark:border-orange-800/40 px-2.5 py-1.5 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-orange-700 dark:text-orange-400 min-w-0">
                        <Scissors className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">Da montare</span>
                      </div>
                      <div className="text-base sm:text-lg font-bold text-orange-600 dark:text-orange-400 leading-none flex-shrink-0">{daMontareCliente.length}</div>
                    </div>
                    {/* Montati — verde: montati nel mese, programmati o meno. Cliccabile. */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleSelectClient(cliente.id, 'montati'); }}
                      className="w-full rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40 hover:border-emerald-400 dark:hover:border-emerald-600 hover:bg-emerald-100/70 dark:hover:bg-emerald-900/40 px-2.5 py-1.5 flex items-center justify-between gap-2 transition-colors text-left"
                      title="Reel montati questo mese (anche se già programmati): clicca per vederli"
                    >
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-emerald-700 dark:text-emerald-400 min-w-0">
                        <Film className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">Montati</span>
                      </div>
                      <div className="text-base sm:text-lg font-bold text-emerald-600 dark:text-emerald-400 leading-none flex-shrink-0">{montatiMese}</div>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {montaggioModal}
      </div>
    );
  }

  // ===== VISTA 2: contenuti del cliente selezionato (da montare / montati nel mese) =====
  const selectedClient = getClienteById(selectedClientId);
  // Da montare: prima quelli con una data di uscita (più vicina in cima), poi
  // gli altri per data di creazione: il video editor vede subito cosa scade.
  const contenutiCliente = contenutiBase
    .filter(c => c.clienteId === selectedClientId)
    .sort((a, b) => {
      const ta = a.stato === StatoContenuto.PROGRAMMATO && a.dataPubblicazione ? new Date(a.dataPubblicazione).getTime() : Infinity;
      const tb = b.stato === StatoContenuto.PROGRAMMATO && b.dataPubblicazione ? new Date(b.dataPubblicazione).getTime() : Infinity;
      if (ta !== tb) return ta - tb;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  const montatiCliente = reelMontatiNelMese(
    state.contenuti.filter(c => c.clienteId === selectedClientId),
    meseCorrente,
    annoCorrente,
  ).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const tabClass = (attiva: boolean, colore: 'orange' | 'emerald') =>
    `inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-medium border transition-colors flex-shrink-0 ${
      attiva
        ? colore === 'orange'
          ? 'bg-orange-600 border-orange-600 text-white'
          : 'bg-emerald-600 border-emerald-600 text-white'
        : 'bg-white dark:bg-gray-700 border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600'
    }`;

  const header = (sottotitolo: string) => (
    <div className="flex items-center gap-2 sm:gap-3">
      <button
        onClick={handleBackToClients}
        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        title="Torna ai clienti"
      >
        <ArrowLeft size={20} />
      </button>
      <div className="min-w-0 flex-1">
        <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ letterSpacing: '-0.02em' }}>
          {selectedClient?.nomeAzienda}
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{sottotitolo}</p>
      </div>
    </div>
  );

  const tabs = (
    <div className="flex gap-2 overflow-x-auto">
      <button type="button" onClick={() => handleCambiaVista('')} className={tabClass(!vistaMontati, 'orange')}>
        <Scissors className="w-4 h-4" /> Da montare
        <span className="text-xs opacity-80">{contenutiCliente.length}</span>
      </button>
      <button type="button" onClick={() => handleCambiaVista('montati')} className={tabClass(vistaMontati, 'emerald')}>
        <Film className="w-4 h-4" /> Montati
        <span className="text-xs opacity-80">{montatiCliente.length}</span>
      </button>
    </div>
  );

  // Badge comuni alle card: stato, uscita programmata, ADV, modifica cliente
  const badgeRiga = (contenuto: Contenuto) => (
    <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
      {contenuto.campagnaAdv && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400" title="Contenuto Campagna ADV">
          <Megaphone size={10} className="flex-shrink-0" /> ADV
        </span>
      )}
      <StatusBadge stato={contenuto.stato} />
      <DriveFlagBadge contenuto={contenuto} />
      <OpusBadge contenuto={contenuto} />
      {contenuto.clientModifiedAt && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800"
          title={`Modificato dal cliente il ${new Date(contenuto.clientModifiedAt).toLocaleDateString('it-IT')}`}>
          Mod. cliente
        </span>
      )}
    </div>
  );

  if (vistaMontati) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {header(`${montatiCliente.length} ${montatiCliente.length === 1 ? 'reel montato' : 'reel montati'} a ${nomeMese}`)}
        {tabs}

        {montatiCliente.length === 0 ? (
          <Card>
            <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="p-4 bg-slate-100 dark:bg-gray-700 rounded-full">
                  <Film className="h-8 w-8 sm:h-10 sm:w-10 text-slate-400 dark:text-slate-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                    Nessun reel montato a {nomeMese}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                    Qui compaiono i contenuti segnati come "Montato".
                  </p>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {montatiCliente.map((contenuto) => (
              <Card
                key={contenuto.id}
                padding="none"
                className="p-3 sm:p-4 lg:p-6 transition-all hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-sm min-w-0"
              >
                <div className="flex justify-between items-center text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 mb-2 sm:mb-3">
                  <span>Aggiornato: {new Date(contenuto.updatedAt).toLocaleDateString('it-IT')}</span>
                  {contenuto.dataPubblicazione && (
                    <span>
                      {contenuto.stato === StatoContenuto.PROGRAMMATO ? 'Esce il' : 'Uscito il'}{' '}
                      {new Date(contenuto.dataPubblicazione).toLocaleDateString('it-IT')}
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight min-w-0" style={{ letterSpacing: '-0.02em' }}>
                      {contenuto.titolo}
                      <AccountDestinazioneBadge contenuto={contenuto} cliente={selectedClient} className="ml-2 align-middle" />
                    </h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                        <Film size={12} /> Montato
                      </span>
                      <StatusBadge stato={contenuto.stato} />
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                    <span className="font-medium text-slate-900 dark:text-slate-100">Hook:</span>{' '}
                    <span className="break-words">
                      {contenuto.hook?.substring(0, 80) || 'N/A'}
                      {contenuto.hook && contenuto.hook.length > 80 && '...'}
                    </span>
                  </div>
                  {contenuto.linkMontaggio && (
                    <div className="text-xs sm:text-sm">
                      <span className="font-medium text-slate-700 dark:text-slate-300">Video montato:</span>{' '}
                      <a
                        href={contenuto.linkMontaggio}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium underline break-all"
                      >
                        Apri link
                      </a>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-1">
                    <Button
                      variant="secondary"
                      onClick={() => apriMontaggio(contenuto, true)}
                      className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Vedi contenuto</span>
                    </Button>
                    {contenuto.stato !== StatoContenuto.PUBBLICATO && contenuto.stato !== StatoContenuto.ARCHIVIATO && (
                      <button
                        onClick={() => annullaMontato(contenuto)}
                        title="Riporta il contenuto in 'Da montare' (non tocca registrato né programmazione)"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 w-full sm:w-auto bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-700 dark:hover:text-orange-400 hover:border-orange-200 dark:hover:border-orange-800"
                      >
                        <Undo2 className="h-4 w-4" />
                        <span>Riporta in editing</span>
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        {montaggioModal}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {header(`${contenutiCliente.length} ${contenutiCliente.length === 1 ? 'contenuto da montare' : 'contenuti da montare'}`)}
      {tabs}

      {contenutiCliente.length === 0 ? (
        <Card>
          <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="p-4 bg-slate-100 dark:bg-gray-700 rounded-full">
                <Scissors className="h-8 w-8 sm:h-10 sm:w-10 text-slate-400 dark:text-slate-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                  Nessun contenuto da montare
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                  I contenuti appariranno qui quando il videomaker li segnerà come registrati.
                </p>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {contenutiCliente.map((contenuto) => {
            const uscita = contenuto.stato === StatoContenuto.PROGRAMMATO ? dataUscita(contenuto) : null;
            const uscitaVicina = uscita !== null && contenuto.dataPubblicazione !== undefined &&
              new Date(contenuto.dataPubblicazione).getTime() - nowEd.getTime() < 3 * 24 * 60 * 60 * 1000;
            return (
              <Card
                key={contenuto.id}
                padding="none"
                className={`p-3 sm:p-4 lg:p-6 transition-all hover:shadow-sm min-w-0 ${
                  uscitaVicina
                    ? 'border-red-300 dark:border-red-700 hover:border-red-400'
                    : 'hover:border-slate-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex justify-between items-center text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 mb-2 sm:mb-3">
                  <span>Registrato: {new Date(contenuto.updatedAt).toLocaleDateString('it-IT')}</span>
                  {uscita && (
                    <span className={`inline-flex items-center gap-1 font-semibold ${uscitaVicina ? 'text-red-500 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      <CalendarClock className="w-3 h-3" /> Esce il {uscita}
                    </span>
                  )}
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight min-w-0" style={{ letterSpacing: '-0.02em' }}>
                      {contenuto.titolo}
                      <AccountDestinazioneBadge contenuto={contenuto} cliente={selectedClient} className="ml-2 align-middle" />
                    </h3>
                    {badgeRiga(contenuto)}
                  </div>

                  <div className="text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                    <span className="font-medium text-slate-900 dark:text-slate-100">Hook:</span>{' '}
                    <span className="break-words">
                      {contenuto.hook?.substring(0, 80) || 'N/A'}
                      {contenuto.hook && contenuto.hook.length > 80 && '...'}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {getClienteById(contenuto.clienteId)?.piattaforme?.map(piattaforma => (
                      <span
                        key={piattaforma}
                        className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300"
                      >
                        {piattaforma}
                      </span>
                    ))}
                  </div>

                  {(contenuto.linkIspirazione || contenuto.copertinaUrl || contenuto.copyPost) && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {contenuto.linkIspirazione && (
                        <a
                          href={contenuto.linkIspirazione}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:underline"
                        >
                          Riferimento
                        </a>
                      )}
                      {contenuto.copertinaUrl && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                          <Image size={12} /> Copertina
                        </span>
                      )}
                      {contenuto.copyPost && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                          Copy
                        </span>
                      )}
                    </div>
                  )}

                  {/* Azioni: Vedi contenuto, Programma (mai per ADV), Montato */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-1 sm:pt-2">
                    <Button
                      variant="secondary"
                      onClick={() => apriMontaggio(contenuto, true)}
                      className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Vedi contenuto</span>
                    </Button>
                    {!contenuto.campagnaAdv && (
                      <button
                        onClick={() => handleProgramma(contenuto)}
                        title={contenuto.stato === StatoContenuto.PROGRAMMATO
                          ? 'Già programmato: apri la Programmazione del cliente'
                          : 'Scegli data e ora in Programmazione — si può programmare anche prima del montaggio'}
                        className={`flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 w-full sm:w-auto border ${
                          contenuto.stato === StatoContenuto.PROGRAMMATO
                            ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400'
                            : 'bg-white dark:bg-gray-700 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                        }`}
                      >
                        <CalendarClock className="h-4 w-4" />
                        <span>{contenuto.stato === StatoContenuto.PROGRAMMATO ? 'Programmato' : 'Programma'}</span>
                      </button>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => apriMontaggio(contenuto)}
                      title="Segna il video come montato"
                      className="w-full sm:w-auto justify-center min-h-[44px] sm:min-h-0"
                    >
                      <Check className="h-4 w-4" />
                      <span>Montato</span>
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {montaggioModal}
    </div>
  );
};

export default Editing;
